import { AS400_CDB_COLUMN, AS400_EXPORT_WORKSHEET_COLUMNS } from '@erp/rpc';
import type { CellRule, CellViolationDetail } from './as400CellRuleKinds';

/**
 * The Item Add side of the cell rules (GEN-2739): which Item Add lines a row's
 * part needs, and the rule that flags a Hazmat / Shelf Life flag set without
 * them. The grid builds the lookup from the rows it has loaded
 * (itemAddPresence); a caller with no lookup skips the rule.
 */

export const ITEM_ADD_ITEM_FIELD = 'IDITEM';
export const ITEM_ADD_LANGUAGE_FIELD = 'IDLANG';

export const SHELF_LIFE_LANGUAGES = ['EN', 'FR'] as const;

export interface ItemAddLine {
  itemNumber: string;
  language: string | null;
  /** The record key the line was read under; none while an unsaved draft has none. */
  cdb?: string;
}

export type ItemAddLookup = (
  itemNumber: string,
  cdb?: string | null,
) => readonly ItemAddLine[];

export function itemAddHasLanguageSlots(): boolean {
  return AS400_EXPORT_WORKSHEET_COLUMNS.ITEM_ADD.some(
    (column) => column.code === ITEM_ADD_LANGUAGE_FIELD,
  );
}

export function normalizePartNumber(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase();
}

export function languageSlot(
  value: string | null | undefined,
): 'EN' | 'FR' | null {
  const code = (value ?? '').trim().toUpperCase();
  if (code === 'EN' || code === 'E') return 'EN';
  if (code === 'FR' || code === 'F') return 'FR';
  return null;
}

export function itemAddPresenceProblem(input: {
  flag: string | null;
  lines: readonly ItemAddLine[];
  languages?: readonly string[];
}): CellViolationDetail | null {
  if (input.flag !== 'Y') return null;
  if (input.lines.length === 0) {
    return { rule: 'item-add-required', missingLanguages: [] };
  }
  if (!input.languages || input.languages.length === 0) return null;
  const present = new Set<'EN' | 'FR'>();
  for (const line of input.lines) {
    const slot = languageSlot(line.language);
    if (slot) present.add(slot);
  }
  const missingLanguages = input.languages.filter((language) => {
    const slot = languageSlot(language);
    return slot === null || !present.has(slot);
  });
  return missingLanguages.length === 0
    ? null
    : { rule: 'item-add-required', missingLanguages };
}

export function itemAddRequiredRule(
  field: string,
  options: { itemField: string; languages?: readonly string[] },
): CellRule {
  return {
    field,
    dependsOn: [options.itemField, AS400_CDB_COLUMN],
    check(value, row) {
      if (!row.itemAdds) return null;
      const itemNumber = row.value(options.itemField);
      const lines = itemNumber
        ? row.itemAdds(itemNumber, row.value(AS400_CDB_COLUMN))
        : [];
      return itemAddPresenceProblem({
        flag: value,
        lines,
        languages: options.languages,
      });
    },
  };
}
